import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { createInstance, createImageVariantConfig } from '../../util/sdkLoader';

// Action Types
const FETCH_RECIPE_REQUEST = 'app/RecipeDetailPage/FETCH_RECIPE_REQUEST';
const FETCH_RECIPE_SUCCESS = 'app/RecipeDetailPage/FETCH_RECIPE_SUCCESS';
const FETCH_RECIPE_ERROR = 'app/RecipeDetailPage/FETCH_RECIPE_ERROR';

// Async Thunk for fetching a single recipe
export const fetchRecipe = createAsyncThunk(
  'recipeDetail/fetchRecipe',
  async (recipeId, { rejectWithValue, extra: sdk }) => {
    try {
      // Recipe-specific image configuration
      const variantPrefix = 'listing-card';
      const aspectWidth = 1;
      const aspectHeight = 1;
      const aspectRatio = aspectHeight / aspectWidth;

      const params = {
        id: recipeId,
        include: ['author', 'author.profileImage', 'images', 'currentStock'],
        expand: true,
        'fields.image': [
          // Scaled variants for large images
          'variants.scaled-small',
          'variants.scaled-medium',
          'variants.scaled-large',
          'variants.scaled-xlarge',

          // Cropped variants for listing thumbnail images
          `variants.${variantPrefix}`,
          `variants.${variantPrefix}-2x`,
          `variants.${variantPrefix}-4x`,
          `variants.${variantPrefix}-6x`,

          // Social media
          'variants.facebook',
          'variants.twitter',

          // Avatars
          'variants.square-small',
          'variants.square-small2x',
        ],
        ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
        ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
        ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
        ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
      };
      // Try to fetch as a listing first (if it's a user-submitted recipe)
      try {
        const response = await sdk.listings.show(params);
        console.log('Recipe response:', JSON.stringify(response.data.data));

        const listing = response.data.data;
        const included = response.data.included || [];

        // Create a map of included resources by ID and type
        const includedMap = new Map();
        included.forEach(item => {
          const id = item.id?.uuid || item.id;
          const key = `${item.type}-${id}`;
          includedMap.set(key, item);
        });

        // Resolve image relationships from included data
        if (listing.relationships?.images?.data) {
          const imageRelationships = listing.relationships.images.data;

          const resolvedImages = imageRelationships
            .map(imageRel => {
              const imageId = imageRel.id?.uuid || imageRel.id;
              const imageType = imageRel.type;
              const imageKey = `${imageType}-${imageId}`;
              const imageData = includedMap.get(imageKey);

              if (!imageData) {
                console.warn('❌ Image not found for key:', imageKey);
                return null;
              }

              return imageData;
            })
            .filter(Boolean);

          // Add resolved images to the listing
          listing.images = resolvedImages;
        } else {
          listing.images = [];
        }

        // Resolve author information
        if (listing.relationships?.author?.data) {
          const authorRel = listing.relationships.author.data;
          const authorId = authorRel.id?.uuid || authorRel.id;
          const authorKey = `${authorRel.type}-${authorId}`;
          const authorData = includedMap.get(authorKey);

          if (authorData) {
            listing.author = authorData;
          }
        }

        return listing;
      } catch (listingError) {
        console.error(
          'Listing fetch failed, possibly not a UUID or deleted:',
          recipeId,
          listingError
        );
        return rejectWithValue(listingError);
      }
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// Initial State
const initialState = {
  recipe: null,
  fetchRecipeInProgress: false,
  fetchRecipeError: null,
};

// Slice
const recipeDetailSlice = createSlice({
  name: 'RecipeDetailPage',
  initialState,
  reducers: {
    // Add any synchronous actions here if needed
    clearRecipe: state => {
      state.recipe = null;
      state.fetchRecipeError = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchRecipe.pending, state => {
        state.fetchRecipeInProgress = true;
        state.fetchRecipeError = null;
      })
      .addCase(fetchRecipe.fulfilled, (state, action) => {
        state.fetchRecipeInProgress = false;
        state.recipe = action.payload;
      })
      .addCase(fetchRecipe.rejected, (state, action) => {
        state.fetchRecipeInProgress = false;
        state.fetchRecipeError = action.payload;
      });
  },
});

// Actions
export const { clearRecipe } = recipeDetailSlice.actions;

// Selectors
export const selectRecipe = state => state.RecipeDetailPage?.recipe;
export const selectFetchRecipeInProgress = state => state.RecipeDetailPage?.fetchRecipeInProgress;
export const selectFetchRecipeError = state => state.RecipeDetailPage?.fetchRecipeError;

// Reducer
export default recipeDetailSlice.reducer;

// Legacy action creators for compatibility
export const fetchRecipeRequest = () => ({ type: FETCH_RECIPE_REQUEST });
export const fetchRecipeSuccess = recipe => ({ type: FETCH_RECIPE_SUCCESS, payload: recipe });
export const fetchRecipeError = error => ({ type: FETCH_RECIPE_ERROR, payload: error });
